/**
 * For You tracking: ordinary trackClick calls (PostHog `utech_click` + the
 * site_events mirror), every action prefixed `foryou_`:
 *   foryou_view                     page shown {problems, keywords, saved}
 *   foryou_section_view             a section scrolled into view (once per visit) {section}
 *   foryou_problem_add / _edit / _remove / _select   {position, chars}
 *   foryou_rec_open                 {tech_id, rank, relevance, match, surface}
 *   foryou_rec_bookmark             {tech_id, on, surface}
 *   foryou_view_all                 {problem_position, count}
 *   foryou_saved_select / _select_all / _open        {tech_id | on, count}
 *   foryou_share_open / _send / _copy / _remove_item {count, recipients}
 *   foryou_keyword_edit / _add / _remove             {keyword}
 *   foryou_featured_open            {tech_id, keyword, week, surface}
 * Bookmarks made here also send the tech card's own `techcard_bookmark` with
 * surface foryou_* so the existing bookmark counts include For You.
 */
import { useEffect, useRef } from 'react';
import { trackClick } from '../../utils/analytics';

export const fyTrack = (action: string, props?: Record<string, unknown>): void => {
  try { trackClick(action, { page: 'foryou', ...(props || {}) }); } catch { /* never break a click */ }
};

/** Fires foryou_section_view once per mount when the section is half visible. */
export function useImpression<T extends HTMLElement>(section: string, enabled = true) {
  const ref = useRef<T | null>(null);
  const done = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el || done.current || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting) && !done.current) {
        done.current = true;
        fyTrack('foryou_section_view', { section });
        io.disconnect();
      }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [section, enabled]);
  return ref;
}
